import React, { useEffect, useState } from 'react';
import { Dialog } from '../../components';
import { Box, Text, Button, FileInput, Spacing } from '../../ui';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useApi } from '../../hooks';
import { ProductIds } from '../../constants';

// Hardcoded for now: every CSV-driven application has Alex Kubicek as the agent of record.
const AGENT_ID = 1;

// The CSV format is dealer-specific, so the upload offers the two dealer captive products.
const CHOICES = [ProductIds.NDI, ProductIds.HDI];

/**
 * Modal entry point for CSV-driven application creation. Two modes:
 *   - `create` (default): user uploads a CSV. On success, returns the parsed form payload
 *     to onSuccess; the caller is responsible for routing the user into SubmitApplicationPage
 *     with that payload as the form seed.
 *   - `replace`: same, but the modal copy warns that re-uploading replaces all locations and
 *     metadata in the in-progress form. No DB writes happen at upload time.
 */
export const CsvUploadModal = ({ open, onClose, onSuccess, mode = 'create', productId = null }) => {
  const { parseCsvIntoForm, getProducts } = useApi();

  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  // The product the user picked; until they pick one, the default below applies.
  const [pickedProductId, setPickedProductId] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setError(null);
      setSubmitting(false);
      setIsDragOver(false);
    } else {
      setPickedProductId(null);
    }
  }, [open, mode, productId]);

  useEffect(() => {
    if (!open) return;
    let current = true;
    Promise.resolve(getProducts())
      .then((listed) => {
        if (current) setProducts(Array.isArray(listed) ? listed : []);
      })
      .catch(() => {
        if (current) setProducts([]);
      });
    return () => {
      current = false;
    };
  }, [open, getProducts]);

  // An option GET /products doesn't return (HDI in a database without it) is not offered.
  const options = CHOICES.map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const offered = (id) => options.some((p) => p.id === id);

  // A replaced form starts from its own product, and keeps it even when it isn't offered; the choice
  // is then hidden, as it is for any product other than NDI or HDI. Otherwise NDI is selected when it
  // is offered, and no product is selected when it isn't: the user picks one. The product posted is
  // always the one selected, and nothing is posted until one is.
  const askedForForm = mode === 'replace' && Boolean(productId);
  const defaultProductId = askedForForm ? productId : offered(ProductIds.NDI) ? ProductIds.NDI : null;
  const productIdToFile = pickedProductId ?? defaultProductId;
  const showChoice = askedForForm ? offered(productId) : options.length > 0;

  const isReplace = mode === 'replace';
  const canSubmit = file && !submitting && Boolean(productIdToFile);

  const acceptFiles = (files) => {
    if (!files || files.length === 0) return;
    const candidate = files[0];
    const isCsv = candidate.name.toLowerCase().endsWith('.csv') || candidate.type === 'text/csv';
    if (!isCsv) {
      setError('Please choose a CSV file');
      return;
    }
    setError(null);
    setFile(candidate);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    acceptFiles(e.dataTransfer && e.dataTransfer.files);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/application-submission-template.csv';
    link.download = 'application-submission-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await parseCsvIntoForm({
        productId: productIdToFile,
        agentId: AGENT_ID,
        file,
      });
      if (result && result.product && result.agent) {
        onSuccess(result);
      } else {
        setError('Server did not return a parsed application payload');
      }
    } catch (err) {
      setError(err.message || 'Failed to parse CSV');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      heading={isReplace ? 'Re-upload CSV' : 'Create Application from CSV'}
      actions={
        <Box flexDirection="row" gap={8}>
          <Button label="Download Template" variant="secondary" onPress={handleDownloadTemplate} disabled={submitting} />
          <Button label="Cancel" variant="secondary" onPress={onClose} disabled={submitting} />
          <Button label={submitting ? 'Processing…' : 'Process CSV'} disabled={!canSubmit} onPress={handleSubmit} />
        </Box>
      }
    >
      {isReplace && (
        <>
          <Text small dim>
            Re-uploading replaces all locations and metadata in the form. Unsaved form changes will be lost.
          </Text>
          <Spacing vertical={1.5} />
        </>
      )}

      {showChoice && (
        <div className="mb-4 flex flex-col gap-2">
          <Label>Product</Label>
          <RadioGroup value={productIdToFile} onValueChange={(value) => setPickedProductId(value)} disabled={submitting}>
            {options.map((product) => (
              <Label key={product.id} className="font-normal">
                <RadioGroupItem value={product.id} />
                {product.name}
              </Label>
            ))}
          </RadioGroup>
        </div>
      )}

      <div
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${isDragOver ? '#5F2994' : '#d0d0d0'}`,
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          background: isDragOver ? '#f5f0fa' : '#fafafa',
          transition: 'background 120ms, border-color 120ms',
        }}
      >
        <Text>{file ? `Selected: ${file.name}` : 'Drag and drop a CSV file here'}</Text>
        <Spacing vertical={1} />
        <FileInput accept=".csv,text/csv" onChangeValue={acceptFiles} emptyText="or click to choose" disabled={submitting} />
      </div>

      {error && (
        <Box mt="$3" padX="$3" padY="$2" borderWidth={1} borderColor="$coral.bright" borderRadius={8}>
          <Text color="$coral.bright">{error}</Text>
        </Box>
      )}
    </Dialog>
  );
};
